import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { BiArrowBack, BiEdit, BiCheckCircle, BiXCircle } from "react-icons/bi";
import { toast } from "react-toastify";

import {
  getQoDetail,
  updateQoStatus,
  clearDetail,
  quotationV2Selector,
} from "./quotationV2Slice";
import { Colors } from "../../../assets/Colors";
import { formatQty } from "../output/QtyInput";

// ── Constants ──────────────────────────────────────────────────────
const STATUS_CONFIG = {
  draft:     { label: "Draft",      bg: "bg-gray-100",   text: "text-gray-600",   dot: "bg-gray-400"   },
  ready:     { label: "Siap JR",    bg: "bg-green-100",  text: "text-green-700",  dot: "bg-green-500"  },
  expired:   { label: "Kadaluarsa", bg: "bg-gray-200",   text: "text-gray-500",   dot: "bg-gray-400"   },
  submitted: { label: "Menunggu Approval", bg: "bg-yellow-100", text: "text-yellow-700", dot: "bg-yellow-500" },
  approved:  { label: "Disetujui",  bg: "bg-green-100",  text: "text-green-700",  dot: "bg-green-500"  },
  rejected:  { label: "Ditolak",    bg: "bg-red-100",    text: "text-red-700",    dot: "bg-red-500"    },
};

const ACTIVITY_LABELS = {
  PORT_SERVICES:            "Port Services",
  RENTAL:                   "Rental",
  WASTE_MANAGEMENT_SERVICES:"Waste Management Services",
  AREA_RENTAL:              "Area Rental",
  MANPOWER:                 "Manpower",
  GENERAL_SERVICE:          "General Service",
};

const SUBTYPE_LABELS = {
  ROOM: "Accomodation / Room", LAUNDRY: "Laundry", MEALS: "Meals",
  WOODEN: "Wooden Dunnage & Pallet", DRILL: "Drill & Fresh Water",
  OTHERS: "Others Service", VEHICLE: "Vehicle Rental",
};

const LINE_GROUP_LABELS = {
  EQUIPMENT: "Equipment", LABOUR: "Labour",
  TWS: "Temporary Waste Storage", TRUCK: "Truck Entry",
};

// ── Helpers ────────────────────────────────────────────────────────
function fmt(n) {
  if (!n && n !== 0) return "—";
  return new Intl.NumberFormat("id-ID").format(n);
}

// ── Small components ───────────────────────────────────────────────
function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] ?? STATUS_CONFIG.draft;
  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium ${cfg.bg} ${cfg.text}`}>
      <span className={`w-2 h-2 rounded-full ${cfg.dot}`} />
      {cfg.label}
    </span>
  );
}

function ServiceTypeBadge({ type }) {
  if (type === "F")
    return <span className="inline-block px-1.5 py-0.5 rounded text-xs font-bold bg-blue-100 text-blue-700">TETAP</span>;
  return <span className="inline-block px-1.5 py-0.5 rounded text-xs font-bold bg-gray-100 text-gray-500">VAR</span>;
}

function InfoField({ label, value, className = "" }) {
  return (
    <div className={className}>
      <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-0.5">{label}</p>
      <p className="text-sm text-gray-800">{value || <span className="text-gray-400">—</span>}</p>
    </div>
  );
}

function SectionCard({ title, children, badge }) {
  return (
    <div className="bg-white rounded-xl border overflow-hidden mb-4">
      <div className="flex items-center gap-2 px-5 py-3 border-b" style={{ background: Colors.primary + "15" }}>
        <span className="font-semibold text-sm" style={{ color: Colors.primary }}>{title}</span>
        {badge}
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}

// ── Line items (kartu) ─────────────────────────────────────────────
function LineTable({ lines, showSubType = false }) {
  if (!lines?.length) return (
    <p className="text-sm text-gray-400 italic">Tidak ada item</p>
  );

  const total = lines.reduce((s, l) => s + (l.Rate * Math.max(l.MinUsage, 0)), 0);

  return (
    <div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {lines.map((l, i) => {
          const isFixed = l.ServiceType === "F";
          const minAmount = l.Rate * Math.max(l.MinUsage, 0);
          return (
            <div key={i} className={`rounded-xl border p-4 ${
              isFixed ? "bg-blue-50/40 border-blue-100" : "bg-white border-gray-200"}`}>
              <div className="flex items-start justify-between gap-2 mb-1">
                <div>
                  <span className="font-semibold text-gray-800 text-sm leading-snug">
                    {l.ItemDisplay || l.ItemName}
                  </span>
                  {showSubType && l.SubType && (
                    <span className="block text-xs text-gray-400">
                      {SUBTYPE_LABELS[l.SubType] || l.SubType}
                    </span>
                  )}
                </div>
                <ServiceTypeBadge type={l.ServiceType} />
              </div>

              <div className="flex items-center gap-4 text-xs text-gray-500 mb-2">
                <span>Min: <strong className="text-gray-700">{formatQty(l.MinUsage)}</strong> {l.SatuanNama || l.SatuanID}</span>
                <span>Rate: <strong className="text-gray-700">Rp {fmt(l.Rate)}</strong></span>
              </div>

              <div className="border-t pt-2 flex items-center justify-between">
                <span className="text-xs text-gray-400">Min. Tagihan</span>
                <span className="font-bold" style={{ color: Colors.primary }}>Rp {fmt(minAmount)}</span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Subtotal grup */}
      <div className="mt-3 flex items-center justify-end gap-3 text-sm border-t pt-3">
        <span className="font-semibold text-gray-600">Total Minimum Tagihan</span>
        <span className="font-bold" style={{ color: Colors.primary }}>Rp {fmt(total)}</span>
      </div>
    </div>
  );
}

// ── Reject modal ───────────────────────────────────────────────────
function RejectModal({ onConfirm, onCancel }) {
  const [note, setNote] = useState("");
  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4">
        <div className="px-6 py-4 border-b">
          <h3 className="font-semibold text-gray-800">Alasan Penolakan</h3>
        </div>
        <div className="px-6 py-4">
          <textarea
            autoFocus
            rows={4}
            value={note}
            onChange={e => setNote(e.target.value)}
            placeholder="Tuliskan alasan penolakan quotation ini…"
            className="w-full border rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 resize-none"
          />
        </div>
        <div className="px-6 py-3 border-t flex justify-end gap-2">
          <button onClick={onCancel}
            className="px-4 py-2 rounded-lg border text-sm text-gray-600 hover:bg-gray-50">
            Batal
          </button>
          <button
            disabled={!note.trim()}
            onClick={() => onConfirm(note)}
            className="px-4 py-2 rounded-lg text-sm font-semibold text-white bg-red-500 hover:bg-red-600 disabled:opacity-40">
            Tolak QO
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main page ──────────────────────────────────────────────────────
export default function QuotationDetailPage() {
  const dispatch  = useDispatch();
  const navigate  = useNavigate();
  const location  = useLocation();
  const qoNo      = location.state?.QoNo ?? null;

  const qoV2      = useSelector(quotationV2Selector);
  const data      = qoV2.detail;

  const [showRejectModal, setShowRejectModal] = useState(false);
  const [actionLoading,   setActionLoading]   = useState(false);

  // ── Load data ──
  useEffect(() => {
    if (qoNo) {
      dispatch(getQoDetail(qoNo));
    }
    return () => { dispatch(clearDetail()); };
  }, [qoNo]); // eslint-disable-line

  if (!qoNo) {
    return (
      <div className="md:ml-64 flex items-center justify-center h-64 text-gray-400">
        <div className="text-center">
          <p className="text-lg mb-2">QO tidak ditemukan</p>
          <button onClick={() => navigate(-1)} className="text-sm text-orange-500 underline">
            Kembali
          </button>
        </div>
      </div>
    );
  }

  if (qoV2.isDetailLoading || !data) {
    return (
      <div className="md:ml-64 flex items-center justify-center h-64 text-gray-400">
        Memuat data…
      </div>
    );
  }

  // ── Group lines ──
  const serviceLines  = (data.Lines ?? []).filter(l => l.LineGroup === "SERVICE");
  const equipLines    = (data.Lines ?? []).filter(l => l.LineGroup === "EQUIPMENT");
  const labourLines   = (data.Lines ?? []).filter(l => l.LineGroup === "LABOUR");
  const twsLines      = (data.Lines ?? []).filter(l => l.LineGroup === "TWS");
  const truckLines    = (data.Lines ?? []).filter(l => l.LineGroup === "TRUCK");

  const activeActivities = (data.Activities ?? []).filter(a => a.IsActive);
  const totalMinBilling  = (data.Lines ?? []).reduce((s, l) =>
    s + (l.Rate * Math.max(l.MinUsage, 0)), 0);

  // ── Status actions ──
  // "Kirim untuk Approval" TIDAK ada di sini — dipindah ke halaman form
  // (QuotationFormPage) supaya pengiriman selalu didahului penyimpanan, sehingga
  // approver tak pernah menilai versi yang belum tersimpan. Halaman ini hanya
  // melayani aksi approver: setujui / tolak / kembalikan ke draft.
  const handleApprove = async () => {
    // 'approved' tidak punya transisi keluar di peta UpdateQoStatus — sekali
    // disetujui tidak ada jalan mundur lewat aplikasi. Itu yang harus disebut.
    const ok = window.confirm(
      `Setujui ${data.QoNo}?\n\n` +
      `Status "Disetujui" bersifat final — tidak bisa dikembalikan ke draft ` +
      `maupun ditolak setelah ini.`
    );
    if (!ok) return;
    setActionLoading(true);
    try {
      await dispatch(updateQoStatus({ QoNo: data.QoNo, Status: "approved" })).unwrap();
      toast.success("QO berhasil disetujui");
      dispatch(getQoDetail(data.QoNo));
    } catch (e) {
      toast.error(e ?? "Gagal menyetujui QO");
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async (note) => {
    setShowRejectModal(false);
    setActionLoading(true);
    try {
      await dispatch(updateQoStatus({ QoNo: data.QoNo, Status: "rejected", Note: note })).unwrap();
      toast.success("QO berhasil ditolak");
      dispatch(getQoDetail(data.QoNo));
    } catch (e) {
      toast.error(e ?? "Gagal menolak QO");
    } finally {
      setActionLoading(false);
    }
  };

  // Backend mengizinkan rejected → draft (UpdateQoStatus). Tanpa tombol ini QO
  // yang ditolak tak bisa diperbaiki lewat aplikasi: tombol Edit pun hanya
  // muncul untuk draft/ready, jadi statusnya mentok permanen.
  const handleBackToDraft = async () => {
    const ok = window.confirm(
      `Kembalikan ${data.QoNo} ke draft?\n\n` +
      `QO bisa diedit lagi, dan harus dikirim ulang untuk approval.`
    );
    if (!ok) return;
    setActionLoading(true);
    try {
      await dispatch(updateQoStatus({ QoNo: data.QoNo, Status: "draft" })).unwrap();
      toast.success("QO dikembalikan ke draft");
      dispatch(getQoDetail(data.QoNo));
    } catch (e) {
      toast.error(e ?? "Gagal mengembalikan QO ke draft");
    } finally {
      setActionLoading(false);
    }
  };

  // ── Render ──
  return (
    <div className="md:ml-64 px-4 md:px-6 pb-20">

      {/* ── Top bar ── */}
      <div className="flex items-center justify-between py-4 mb-4">
        <button onClick={() => navigate(-1)}
          className="flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900">
          <BiArrowBack className="text-lg" /> Kembali
        </button>

        <div className="flex items-center gap-2">
          <StatusBadge status={data.Status} />

          {/* Draft / Ready: Edit + Buat JR — TIDAK untuk pemberi approval.
              Perannya menilai QO, bukan mengubah isinya atau menurunkannya jadi
              JR; approver yang bisa mengedit berarti bisa menyetujui versi yang
              ia tulis sendiri. Ini penyembunyian UI saja — pintu tulisnya
              (updateQo, insert JR) tidak dijaga per-peran, jadi jangan
              diperlakukan sebagai kendali keamanan. */}
          {(data.Status === "draft" || data.Status === "ready") && !data.CanApprove && (
            <>
              <button
                onClick={() => navigate("/edit-quotation-or-contract", { state: { QoNo: data.QoNo } })}
                className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg border text-sm font-medium text-orange-600 border-orange-300 hover:bg-orange-50"
              >
                <BiEdit /> Edit
              </button>
              <button
                onClick={() => navigate("/operasional-job-request-form", { state: { QoNo: data.QoNo } })}
                className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-white text-sm font-semibold"
                style={{ background: Colors.primary }}
              >
                <BiCheckCircle className="text-base" /> Buat JR
              </button>
            </>
          )}

          {/* QO approved: bagian FM-IT/BD tetap boleh mengedit (CanEditApproved
              dihitung backend dari Bagian karyawan; UpdateQo menegakkannya). */}
          {data.Status === "approved" && data.CanEditApproved && (
            <button
              onClick={() => navigate("/edit-quotation-or-contract", { state: { QoNo: data.QoNo } })}
              className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg border text-sm font-medium text-orange-600 border-orange-300 hover:bg-orange-50"
            >
              <BiEdit /> Edit
            </button>
          )}

          {/* Tombol alur status. Sebelumnya handleApprove sudah ada tapi TIDAK
              PERNAH dirender, dan setShowRejectModal(true) tak pernah dipanggil —
              akibatnya QO yang benar-benar dibuat dari nol mentok selamanya.
              Tidak ketahuan lama karena seluruh QO lain lahir langsung 'approved'
              dari migrasi TQuatation, bukan lewat jalur ini. Peta transisi yang
              ditegakkan backend (UpdateQoStatus): draft→submitted,
              submitted→approved|rejected, rejected→draft. Sisi draft→submitted
              dilayani halaman form, bukan di sini. */}

          {/* Setujui/Tolak hanya untuk pemberi approval Quotation (Setup › Approval,
              grup 'Quotation'). CanApprove dihitung backend dari nik yang login —
              jangan diganti dengan pencocokan di sisi klien, karena itu cuma
              menyembunyikan tombol tanpa menghalangi siapa pun memanggil API-nya.
              Penjaga sesungguhnya ada di UpdateQoStatus; ini sekadar agar user
              tak disodori tombol yang pasti ditolak. */}
          {data.Status === "submitted" && (data.CanApprove ? (
            <>
              <button
                onClick={handleApprove}
                disabled={actionLoading}
                className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg bg-green-600 hover:bg-green-700 text-white text-sm font-semibold disabled:opacity-60"
              >
                <BiCheckCircle className="text-base" />
                {actionLoading ? "Memproses…" : "Setujui"}
              </button>
              <button
                onClick={() => setShowRejectModal(true)}
                disabled={actionLoading}
                className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg border text-sm font-medium text-red-600 border-red-300 hover:bg-red-50 disabled:opacity-60"
              >
                <BiXCircle className="text-base" /> Tolak
              </button>
            </>
          ) : (
            <span className="text-xs text-gray-500 px-2">
              {data.ApproverNames
                ? `Menunggu approval: ${data.ApproverNames}`
                : "Approval Quotation belum di-setup (Setup › Approval)"}
            </span>
          ))}

          {data.Status === "rejected" && (
            <button
              onClick={handleBackToDraft}
              disabled={actionLoading}
              className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg border text-sm font-medium text-orange-600 border-orange-300 hover:bg-orange-50 disabled:opacity-60"
            >
              <BiEdit /> {actionLoading ? "Memproses…" : "Kembalikan ke Draft"}
            </button>
          )}
        </div>
      </div>

      {/* ── Reject note banner ── */}
      {data.Status === "rejected" && data.RejectReason && (
        <div className="mb-4 px-4 py-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">
          <span className="font-semibold">Alasan penolakan: </span>{data.RejectReason}
        </div>
      )}

      {/* ── Header info ── */}
      <SectionCard title="Informasi Kontrak">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
          <InfoField label="No. Quotation"   value={data.QoNo} />
          <InfoField label="No. Kontrak"     value={data.NoKontrak} />
          <InfoField label="Nama Klien"      value={data.NamaKlien} />
          <InfoField label="Deskripsi"       value={data.WorkDesc} />
          <InfoField label="Tanggal Mulai"   value={data.StartDate} />
          <InfoField label="Masa Berlaku"    value={data.ExpireDate} />
          <InfoField label="Dibuat oleh"     value={data.CreateBy} />
          <InfoField label="Tanggal Dibuat"  value={data.CreateDate} />
          {data.ApprovedBy && (
            <InfoField label="Disetujui oleh" value={`${data.ApprovedBy} — ${data.ApprovedDate}`} />
          )}
        </div>

        {/* Activity chips */}
        <div className="mt-4 pt-4 border-t">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-2">Aktivitas</p>
          <div className="flex flex-wrap gap-2">
            {activeActivities.length === 0 ? (
              <span className="text-sm text-gray-400">—</span>
            ) : (
              activeActivities.map(a => (
                <span key={a.ActID}
                  className="px-2.5 py-1 rounded-full text-xs font-medium bg-orange-50 text-orange-700 border border-orange-200">
                  {ACTIVITY_LABELS[a.ActivityName] ?? a.ActivityName}
                </span>
              ))
            )}
          </div>
        </div>

        {/* Min billing summary */}
        <div className="mt-4 pt-4 border-t flex items-center justify-between">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">
            Total Minimum Tagihan (estimasi per periode)
          </p>
          <p className="text-lg font-bold" style={{ color: Colors.primary }}>
            Rp {fmt(totalMinBilling)}
          </p>
        </div>
      </SectionCard>

      {/* ── Amandemen (Additional Quotation) yang menginduk ke QO ini ──
           Amandemen sengaja TIDAK muncul di daftar quotation — daftar itu hanya
           menampilkan QoType='1' supaya tetap terbaca sebagai daftar kontrak.
           Di sinilah tempatnya: dibaca bersama induknya, bukan sendirian. */}
      {(data.Amandemen ?? []).length > 0 && (
        <SectionCard
          title="Amandemen"
          badge={
            <span className="ml-auto text-xs text-gray-500">
              {data.Amandemen.length} dokumen
            </span>
          }
        >
          <div className="divide-y divide-gray-100">
            {data.Amandemen.map(a => (
              <div
                key={a.QoID}
                className="flex flex-wrap items-center gap-x-4 gap-y-1 py-3 first:pt-0 last:pb-0"
              >
                <span className="font-mono text-xs text-gray-700">{a.QoNo}</span>
                <StatusBadge status={a.Status} />
                <span className="text-sm text-gray-600">
                  {a.StartDate} <span className="text-gray-400 mx-0.5">&rarr;</span> {a.ExpireDate}
                </span>
                {a.WorkDesc && (
                  <span className="text-sm text-gray-500 truncate max-w-[280px]" title={a.WorkDesc}>
                    {a.WorkDesc}
                  </span>
                )}
                {a.JrCount > 0 && (
                  <span className="text-[11px] text-gray-400">{a.JrCount} JR</span>
                )}
                <button
                  onClick={() => navigate("/quotation-detail-or-contract", { state: { QoNo: a.QoNo } })}
                  className="ml-auto px-3 py-1 rounded-lg border text-xs font-medium text-gray-600 border-gray-300 hover:bg-gray-50"
                >
                  Buka
                </button>
              </div>
            ))}
          </div>
        </SectionCard>
      )}

      {/* ── Service lines per activity ── */}
      {activeActivities.map(act => {
        const lines = serviceLines.filter(l => l.ActivityName === act.ActivityName);
        if (!lines.length) return null;

        const isGeneral = act.ActivityName === "GENERAL_SERVICE";

        return (
          <SectionCard
            key={act.ActID}
            title={ACTIVITY_LABELS[act.ActivityName] ?? act.ActivityName}
            badge={
              <span className="ml-auto text-xs text-gray-500">
                {lines.length} item
              </span>
            }
          >
            {isGeneral ? (
              // General Service: group by SubType
              Object.entries(
                lines.reduce((acc, l) => {
                  const key = l.SubType || "OTHER";
                  (acc[key] = acc[key] || []).push(l);
                  return acc;
                }, {})
              ).map(([subType, subLines]) => (
                <div key={subType} className="mb-5 last:mb-0">
                  <div className="flex items-center gap-2 mb-2">
                    <p className="text-sm font-semibold text-gray-700">
                      {SUBTYPE_LABELS[subType] ?? subType}
                    </p>
                    <ServiceTypeBadge type={subLines[0]?.ServiceType} />
                    {subLines[0]?.ServiceType === "F" && (
                      <span className="text-xs text-blue-500 italic">
                        otomatis masuk JR setiap hari
                      </span>
                    )}
                  </div>
                  <LineTable lines={subLines} />
                </div>
              ))
            ) : (
              <LineTable lines={lines} />
            )}
          </SectionCard>
        );
      })}

      {/* ── Supporting sections ── */}
      {[
        { lines: equipLines,  label: "Equipment",                group: "EQUIPMENT" },
        { lines: labourLines, label: "Labour",                   group: "LABOUR"    },
        { lines: twsLines,    label: "Temporary Waste Storage",  group: "TWS"       },
        { lines: truckLines,  label: "Truck Entry",              group: "TRUCK"     },
      ].filter(s => s.lines.length > 0).map(({ lines, label, group }) => (
        <SectionCard key={group} title={label}
          badge={<span className="ml-auto text-xs text-gray-500">{lines.length} item</span>}>
          <LineTable lines={lines} />
        </SectionCard>
      ))}

      {/* ── Reject modal ── */}
      {showRejectModal && (
        <RejectModal
          onConfirm={handleReject}
          onCancel={() => setShowRejectModal(false)}
        />
      )}
    </div>
  );
}
